import React from 'react'
import { useEffect, useState } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
    ChevronDownIcon,
    ChevronUpIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import { toolsFormSchema, ToolsFormValues } from '../validation'
import {
    defaultOnboardStepRows,
    onboardStepRowsFromTool,
    serializeOnboardStepsForApi,
} from '../toolsOnboardingConfig'
import InputField from 'components/styledFormElements/InputField'
import { useGetToolDetail, useAddTool, useUpdateTool } from 'hooks/tools'
import { useUploadFiles } from 'hooks/upload'
import { useNotificationBar } from 'context/notificationContext'
import {
    getErrorResponse,
    getImageSrc,
    getRelativeIconPath,
    removeCloudFrontPrefix,
} from 'services/util'
import { Loader } from 'components/loader'
import { SearchableItemTypeDropdown } from './SearchableItemTypeDropdown'
import FileInputUploadField from 'components/styledFormElements/FileInputUploadField'
import { useGetIcons } from 'hooks/useIconMaster'
import MediaAssetsPanel from './MediaAssetsPanel'
import QuestionsPanel from './QuestionsPanel'
import {
    newQuestionClientKey,
    parseToolQuestionsFromApi,
    serializeToolQuestionsForApi,
    type ToolQuestionEntry,
} from '../toolQuestions'
import CreateQuestionModal, {
    type CreateQuestionSavePayload,
} from './CreateQuestionModal'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import { X } from 'lucide-react'

function chipImageSrc(chipImage: string): string | null {
    const s = chipImage?.trim()
    if (!s) return null
    if (s.startsWith('data:') || s.startsWith('http')) return s
    return getImageSrc(s)
}

const TOOL_HEADER_IMAGE_UPLOAD_PATH = 'tools/onboarding'

const normalizeHeaderImageKey = (value: string): string => {
    const s = value?.trim()
    if (!s) return ''
    if (s.startsWith('blob:') || s.startsWith('data:')) return s
    if (s.startsWith('http://') || s.startsWith('https://')) {
        try {
            return new URL(s).pathname.replace(/^\/+/, '')
        } catch {
            return s
        }
    }
    return s
}

const normalizeIconKey = (value: string): string => {
    const s = String(value ?? '').trim()
    if (!s) return ''
    return getRelativeIconPath(removeCloudFrontPrefix(s))
}

interface ToolsFormProps {
    editToolId?: string
    onCancel: () => void
    onSuccess: () => void
}

const defaultValues: ToolsFormValues = {
    toolName: '',
    toolType: 'LLM',
    webview: 'NATIVE',
    webviewUrl: "",
    display: 'entity',
    description: '',
    toolPrompt: '',
    itemType: '',
    items: '',
    itemName: '',
    icon: '',
    primaryColor: '',
    secondaryColor: '',
    status: 1,
    onboardHeader: '',
    onboardHeaderImage: '',
    onboardSubHeader: '',
    ctaButtonText: '',
    onboardSteps: defaultOnboardStepRows(),
}

const ToolsForm: React.FC<ToolsFormProps> = ({
    editToolId,
    onCancel,
    onSuccess,
}) => {
    const notification = useNotificationBar()
    const isEditMode = !!editToolId
    const [selectedItem, setSelectedItem] = useState<any>(null)
    const [createQuestionsOpen, setCreateQuestionsOpen] = useState(false)
    const [editingQuestionClientKey, setEditingQuestionClientKey] = useState<
        string | null
    >(null)
    const [questions, setQuestions] = useState<ToolQuestionEntry[]>([])
    const [headerImageFile, setHeaderImageFile] = useState<File | null>(null)

    const uploadMutation = useUploadFiles()
    const editingQuestion = editingQuestionClientKey
        ? questions.find(
            (q) => q.clientKey === editingQuestionClientKey
        ) ?? null
        : null

    const handleCreateQuestion = () => {
        setEditingQuestionClientKey(null)
        setCreateQuestionsOpen(true)
    }

    const handleEditQuestion = (clientKey: string) => {
        setEditingQuestionClientKey(clientKey)
        setCreateQuestionsOpen(true)
    }

    const handleDeleteQuestion = (clientKey: string) => {
        setQuestions((prev) => prev.filter((x) => x.clientKey !== clientKey))
        if (editingQuestionClientKey === clientKey) {
            setCreateQuestionsOpen(false)
            setEditingQuestionClientKey(null)
        }
    }
    const methods = useForm<ToolsFormValues>({
        resolver: yupResolver(toolsFormSchema),
        defaultValues,
        mode: 'onChange',
    })

    const {
        register,
        handleSubmit,
        control,
        reset,
        setValue,
        getValues,
        formState: { errors },
        watch,
    } = methods

    const { fields: onboardStepFields, append, remove, move } = useFieldArray({
        control,
        name: 'onboardSteps',
    })

    const display = watch('display')
    const isWebviewSystem = watch('webview') === 'WEBVIEW'
    const { data: iconsResponse } = useGetIcons()
    const icons = Array.isArray(iconsResponse?.data?.results)
        ? iconsResponse.data.results
        : []

    const { data: toolData, isFetching: loadingTool } = useGetToolDetail(
        editToolId || '',
        {
            enabled: !!editToolId,
        }
    )

    const { mutate: addTool, isLoading: adding } = useAddTool()
    const { mutate: updateTool, isLoading: updating } = useUpdateTool()

    const loading = adding || updating
    useEffect(() => {
        if (!editToolId || !toolData?.data) return

        const tool = toolData.data

        reset({
            toolName: tool.toolName || '',
            toolType: tool.tool_type || 'LLM',
            webview: tool.systemType ?? (tool.webview ? 'WEBVIEW' : 'NATIVE'),
            webviewUrl: tool.webviewUrl || "",
            display: tool.display || 'entity',
            description: tool.description || '',
            toolPrompt: tool.toolPrompt || '',
            itemType: tool.item_type || '',
            items: tool.items || '',
            itemName: tool.item_name || '',
            icon: tool.icon || '',
            primaryColor: tool.primaryColor || tool.primary_color || '',
            secondaryColor: tool.secondaryColor || tool.secondary_color || '',
            status: tool.status || 1,
            onboardHeader: tool.onboardHeader ?? tool.onboard_header ?? '',
            onboardHeaderImage: normalizeHeaderImageKey(
                String(
                    tool.onboardHeaderImage ??
                    tool.onboard_header_image ??
                    ''
                ).trim()
            ),
            onboardSubHeader:
                tool.onboardSubHeader ??
                tool.onboard_sub_header ??
                tool?.onboardSteps?.[0]?.description ??
                tool?.onboard_steps?.[0]?.description ??
                '',
            ctaButtonText: tool.ctaButtonText ?? tool.cta_button_text ?? '',
            onboardSteps: onboardStepRowsFromTool(tool),
        })

        if (tool.item_type && tool.items) {
            setSelectedItem({
                id: tool.items,
                name: tool.item_name || tool.items,
                type: tool.item_type,
            })
        }

        setQuestions(parseToolQuestionsFromApi(tool))
        setHeaderImageFile(null)
    }, [toolData?.data, reset, editToolId])

    useEffect(() => {
        if (!editToolId) {
            reset(defaultValues)
            setSelectedItem(null)
            setQuestions([])
            setHeaderImageFile(null)
        }
    }, [editToolId, reset])

    const handleItemTypeSelect = (item: any) => {
        if (item) {
            setSelectedItem(item)
            setValue('itemType', item.type, { shouldValidate: true })
            setValue('items', item.id, { shouldValidate: true })
            setValue('itemName', item.name, { shouldValidate: true })
        } else {
            setSelectedItem(null)
            setValue('items', '', { shouldValidate: true })
            setValue('itemName', '', { shouldValidate: true })
        }
    }

    const handleDisplayChange = (nextDisplay: string) => {
        setValue('display', nextDisplay, { shouldValidate: true })
        if (nextDisplay === 'global') {
            setValue('itemType', '', { shouldValidate: true })
            setValue('items', '', { shouldValidate: true })
            setValue('itemName', '', { shouldValidate: true })
            setSelectedItem(null)
        }
    }

    const onboardHeaderImageValue = watch('onboardHeaderImage')

    const onHeaderImageFileSelect = (file: File | null) => {
        setHeaderImageFile(file)
        if (!file) {
            setValue('onboardHeaderImage', '', { shouldValidate: true })
            return
        }
        if (!file.type.startsWith('image/')) {
            setHeaderImageFile(null)
            setValue('onboardHeaderImage', '', { shouldValidate: true })
            return
        }
        setValue('onboardHeaderImage', file.name, { shouldValidate: true })
    }

    /** Binder for file input only; path lives in `onboardHeaderImage`, new file in `headerImageFile`. */
    const headerImageFileRegister = {
        name: '__toolOnboardHeaderImageFile',
        onChange: () => { },
        onBlur: () => { },
        ref: () => { },
    } as any

    const onSubmit = async (values: ToolsFormValues) => {
        const onError = (errorRes: any) => {
            notification.push(getErrorResponse(errorRes))
        }

        let onboardHeaderImageKey = String(
            values.onboardHeaderImage ?? ''
        ).trim()

        if (headerImageFile) {
            try {
                const fd = new FormData()
                fd.append('file', headerImageFile)
                fd.append('customPath', TOOL_HEADER_IMAGE_UPLOAD_PATH)
                const res = await uploadMutation.mutateAsync({ data: fd })
                onboardHeaderImageKey = res?.data?.data?.key || ''
                if (!onboardHeaderImageKey) {
                    notification.push({
                        title: 'Upload failed',
                        type: 'error',
                        message: 'Header image upload did not return a path.',
                    })
                    return
                }
            } catch (errorRes: any) {
                notification.push(getErrorResponse(errorRes))
                return
            }
        } else if (onboardHeaderImageKey) {
            onboardHeaderImageKey = normalizeHeaderImageKey(
                removeCloudFrontPrefix(onboardHeaderImageKey)
            )
        }

        const iconPath = getRelativeIconPath(values.icon)
        const serializedOnboardSteps = serializeOnboardStepsForApi(
            values.onboardSteps
        ).map((step, index) => {
            const rawIcon = values.onboardSteps?.[index]?.icon || ''
            const stepIconPath = getRelativeIconPath(rawIcon)
            return {
                ...step,
                icon: stepIconPath || rawIcon,
            }
        })
        const data = {
            ...values,
            systemType: values.webview,
            toolType: values.toolType,
            webviewUrl: values.webview === 'WEBVIEW' ? values.webviewUrl ?? '' : '',
            icon: iconPath || values.icon,
            onboardHeaderImage: onboardHeaderImageKey,
            onboardSteps: serializedOnboardSteps,
            questions: serializeToolQuestionsForApi(questions),
        }

        if (editToolId) {
            updateTool(
                { id: editToolId, data },
                {
                    onSuccess: () => {
                        setHeaderImageFile(null)
                        notification.push({
                            title: 'Tool Updated',
                            type: 'success',
                            message: 'Tool updated successfully',
                        })
                        onSuccess()
                    },
                    onError,
                }
            )
        } else {
            addTool(
                { data },
                {
                    onSuccess: () => {
                        setHeaderImageFile(null)
                        notification.push({
                            title: 'Tool Created',
                            type: 'success',
                            message: 'Tool created successfully',
                        })
                        onSuccess()
                    },
                    onError,
                }
            )
        }
    }

    return loadingTool ? (
        <div className="flex justify-center items-center min-h-[40vh]">
            <Loader />
        </div>
    ) : (
        <>
            <CreateQuestionModal open={createQuestionsOpen}
                initialData={editingQuestion}
                onClose={() => {
                    setCreateQuestionsOpen(false)
                    setEditingQuestionClientKey(null)
                }}
                onSave={(data: CreateQuestionSavePayload) => {
                    if (editingQuestionClientKey) {
                        setQuestions((prev) =>
                            prev.map((q) =>
                                q.clientKey ===
                                    editingQuestionClientKey
                                    ? { ...q, ...data }
                                    : q
                            )
                        )
                        notification.push({
                            title: 'Question updated',
                            type: 'success',
                            message: `Updated with ${data.options.length} option row(s).`,
                        })
                    } else {
                        setQuestions((prev) => [
                            ...prev,
                            {
                                ...data,
                                clientKey: newQuestionClientKey(),
                            },
                        ])
                        notification.push({
                            title: 'Question saved',
                            type: 'success',
                            message: `Added with ${data.options.length} option row(s).`,
                        })
                    }
                }} />
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Basic Information
                        </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField
                            label="Tool Name"
                            required
                            register={register('toolName', {
                                required: 'Tool name is required',
                            })}
                            error={errors.toolName?.message}
                            placeholder="Enter tool name"
                        />

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Tool Type <span className="text-red-500">*</span>
                            </label>
                            <Controller
                                name="toolType"
                                control={control}
                                render={({ field }) => (
                                    <select
                                        {...field}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
                                    >
                                        <option value="LLM">LLM</option>
                                        <option value="Voice">Voice</option>
                                        <option value="Ai_image">AI Image</option>
                                        <option value="Ai_idea">AI Idea</option>
                                        <option value="Ai_script">AI Script</option>
                                    </select>
                                )}
                            />
                            {errors.toolType && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.toolType.message}
                                </p>
                            )}
                        </div>
                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                System Type <span className="text-red-500">*</span>
                            </label>
                            <Controller
                                name="webview"
                                control={control}
                                render={({ field }) => (
                                    <select
                                        value={field.value || 'NATIVE'}
                                        onChange={(e) => {
                                            const v = e.target.value
                                            field.onChange(v)
                                            if (v !== 'WEBVIEW') {
                                                setValue('webviewUrl', '', { shouldValidate: true })
                                            }
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
                                    >
                                        <option value="NATIVE">Native</option>
                                        <option value="WEBVIEW">Webview</option>
                                    </select>
                                )}
                            />
                            {errors.webview && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.webview.message}
                                </p>
                            )}
                        </div>
                        <InputField
                            label="WebView URL"
                            register={register('webviewUrl')}
                            error={errors.webviewUrl?.message}
                            placeholder="Enter WebView URL"
                            disabled={!isWebviewSystem}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Display <span className="text-red-500">*</span>
                            </label>
                            <Controller
                                name="display"
                                control={control}
                                render={({ field }) => (
                                    <select
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e)
                                            handleDisplayChange(e.target.value)
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
                                    >
                                        <option value="entity">Entity</option>
                                        <option value="global">Global</option>
                                    </select>
                                )}
                            />
                            {errors.display && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.display.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Status <span className="text-red-500">*</span>
                            </label>
                            <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                    <select
                                        {...field}
                                        value={field.value}
                                        onChange={(e) =>
                                            field.onChange(Number(e.target.value))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
                                    >
                                        <option value={1}>Active</option>
                                        <option value={0}>Inactive</option>
                                    </select>
                                )}
                            />
                            {errors.status && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.status.message}
                                </p>
                            )}
                        </div>
                    </div>

                    {display === 'entity' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Item Type <span className="text-red-500">*</span>
                                </label>
                                <Controller
                                    name="itemType"
                                    control={control}
                                    render={({ field }) => (
                                        <select
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => {
                                                field.onChange(e)
                                                setSelectedItem(null)
                                                setValue('items', '', {
                                                    shouldValidate: true,
                                                })
                                                setValue('itemName', '', {
                                                    shouldValidate: true,
                                                })
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
                                        >
                                            <option value="">Select item type</option>
                                            <option value="category">Category</option>
                                            <option value="collection">Collection</option>
                                            <option value="show">Show</option>
                                        </select>
                                    )}
                                />
                                {errors.itemType && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {errors.itemType.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Search & Select {watch('itemType') || 'Item'}{' '}
                                    <span className="text-red-500">*</span>
                                </label>
                                {watch('itemType') ? (
                                    <SearchableItemTypeDropdown
                                        itemType={
                                            watch('itemType') as
                                            | 'category'
                                            | 'collection'
                                            | 'show'
                                        }
                                        value={selectedItem}
                                        onSelect={handleItemTypeSelect}
                                        placeholder={`Search ${watch('itemType')}...`}
                                    />
                                ) : (
                                    <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400">
                                        Please select an item type first
                                    </div>
                                )}
                                {errors.items && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {errors.items.message}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                {...register('description')}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
                                placeholder="Enter tool description"
                            />
                            {errors.description && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.description.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Tool Prompt
                            </label>
                            <textarea
                                {...register('toolPrompt')}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
                                placeholder="Enter tool prompt"
                            />
                            {errors.toolPrompt && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.toolPrompt.message}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 items-stretch">
                    <div className="h-full">
                        <MediaAssetsPanel
                            control={control}
                            errors={errors}
                            icons={icons}
                        /></div>
                    <div className="h-full">
                        <QuestionsPanel
                            questions={questions}
                            onCreate={handleCreateQuestion}
                            onEdit={handleEditQuestion}
                            onDelete={handleDeleteQuestion}
                            chipImageSrc={chipImageSrc}
                        />
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                        Onboarding — Header
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField
                            label="Onboarding header"
                            required
                            register={register('onboardHeader')}
                            error={errors.onboardHeader?.message}
                            placeholder="Main headline"
                        />
                        <InputField
                            label="Onboarding sub-header"
                            required
                            register={register('onboardSubHeader')}
                            error={errors.onboardSubHeader?.message}
                            placeholder="Supporting line under the header"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 items-start">
                        <div className="space-y-2 min-w-0">
                            <FileInputUploadField
                                label="Upload header image"
                                required
                                fieldName="onboardHeaderImage"
                                register={headerImageFileRegister}
                                errors={errors}
                                watchFile={headerImageFile}
                                existingFile={
                                    onboardHeaderImageValue &&
                                        !headerImageFile
                                        ? chipImageSrc(onboardHeaderImageValue)
                                        : null
                                }
                                onFileSelect={onHeaderImageFileSelect}
                            />
                        </div>
                        <InputField
                            label="CTA button text"
                            required
                            register={register('ctaButtonText')}
                            error={errors.ctaButtonText?.message}
                            placeholder="e.g. Get started"
                        />
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Onboarding — Steps
                        </h4>
                        <button
                            type="button"
                            onClick={() => append({ icon: '', title: '', description: '' })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add step
                        </button>
                    </div>

                    {errors.onboardSteps &&
                        typeof errors.onboardSteps.message === 'string' ? (
                        <p className="text-red-500 text-xs mb-2">
                            {errors.onboardSteps.message}
                        </p>
                    ) : null}

                    <ul className="space-y-2">
                        {onboardStepFields.map((item, index) => (
                            <li
                                key={item.id}
                                className="rounded-md border border-gray-200 dark:border-slate-600 p-2.5 bg-gray-50/60 dark:bg-slate-900/25"
                            >
                                <div className="flex flex-wrap items-start gap-2 sm:gap-3">
                                    <div
                                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-800 dark:bg-blue-950/60 dark:text-blue-200"
                                        aria-hidden
                                    >
                                        {index + 1}
                                    </div>

                                    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start">

                                        <div className="min-w-0 flex-1 basis-[10rem]">
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Icon <span className="text-red-700">*</span>
                                                </label>

                                                <Controller
                                                    name={`onboardSteps.${index}.icon`}
                                                    control={control}
                                                    render={({ field }) => {
                                                        const iconValue = field.value;
                                                        return (

                                                            <div className="space-y-2">
                                                                {/* {(() => {
                                                                    const normalizedFieldValue = normalizeIconKey(
                                                                        field.value
                                                                    )
                                                                    const selectedIcon = normalizedFieldValue
                                                                        ? icons.find((icon: any) => {
                                                                            const candidates = [
                                                                                icon.path,
                                                                                icon.image,
                                                                                icon.icon,
                                                                            ]
                                                                            return candidates.some((candidate) => {
                                                                                const normalizedCandidate =
                                                                                    normalizeIconKey(
                                                                                        candidate
                                                                                    )
                                                                                return (
                                                                                    !!normalizedCandidate &&
                                                                                    normalizedCandidate ===
                                                                                    normalizedFieldValue
                                                                                )
                                                                            })
                                                                        })
                                                                        : null
                                                                    return (
                                                                        <IconDropdown
                                                                            value={selectedIcon?.id || null}
                                                                            onChange={(selectedIcon: any) => {
                                                                                const iconKey =
                                                                                    selectedIcon?.path ||
                                                                                    selectedIcon?.image ||
                                                                                    selectedIcon?.icon ||
                                                                                    ''
                                                                                field.onChange(
                                                                                    normalizeIconKey(
                                                                                        iconKey
                                                                                    )
                                                                                )
                                                                            }}
                                                                        />
                                                                    )
                                                                })()} */}
                                                                {iconValue ? (
                                                                    <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                                        <div className="flex items-center gap-2">
                                                                            <img
                                                                                src={
                                                                                    iconValue.startsWith('http')
                                                                                        ? iconValue
                                                                                        : getImageSrc(iconValue)
                                                                                }
                                                                                alt="Selected icon"
                                                                                className="w-6 h-6 object-contain"
                                                                            />
                                                                            <span className="text-sm text-gray-700 dark:text-gray-200">
                                                                                Icon selected
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                field.onChange('');
                                                                            }}
                                                                            className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                                        >
                                                                            <X size={14} />
                                                                        </button>
                                                                    </div>
                                                                )
                                                                    : (
                                                                        <IconDropdown
                                                                            value={iconValue}
                                                                            onChange={(selectedIcon: any) => {
                                                                                const iconKey =
                                                                                    selectedIcon?.path ||
                                                                                    selectedIcon?.image ||
                                                                                    selectedIcon?.icon ||
                                                                                    '';

                                                                                field.onChange(iconKey);
                                                                            }}
                                                                        />
                                                                    )}
                                                            </div>
                                                        )
                                                    }}
                                                />

                                                {errors.onboardSteps?.[index]?.icon ? (
                                                    <p className="text-red-500 text-xs mt-1">
                                                        {errors.onboardSteps?.[index]?.icon?.message}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="min-w-0 flex-1 basis-[10rem]">
                                            <InputField
                                                label="Title"
                                                required
                                                register={register(
                                                    `onboardSteps.${index}.title`
                                                )}
                                                error={
                                                    errors.onboardSteps?.[index]
                                                        ?.title?.message as string
                                                }
                                                placeholder="Step title"
                                            />
                                        </div>
                                        <div className="min-w-0 flex-1 basis-[12rem]">
                                            <InputField
                                                label="Description"
                                                isTextarea
                                                row={2}
                                                texAreaClass="max-h-40 min-h-[2.75rem]"
                                                register={register(
                                                    `onboardSteps.${index}.description`
                                                )}
                                                error={
                                                    errors.onboardSteps?.[index]
                                                        ?.description
                                                        ?.message as string
                                                }
                                                placeholder="Step description"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex shrink-0 items-center gap-0.5 self-center sm:self-start sm:pt-5">
                                        <button
                                            type="button"
                                            disabled={index === 0}
                                            onClick={() => move(index, index - 1)}
                                            className="rounded p-1 text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-30"
                                            aria-label="Move step up"
                                        >
                                            <ChevronUpIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            type="button"
                                            disabled={
                                                index === onboardStepFields.length - 1
                                            }
                                            onClick={() => move(index, index + 1)}
                                            className="rounded p-1 text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-30"
                                            aria-label="Move step down"
                                        >
                                            <ChevronDownIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            type="button"
                                            disabled={onboardStepFields.length <= 1}
                                            onClick={() => remove(index)}
                                            className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:pointer-events-none disabled:opacity-30"
                                            aria-label="Remove step"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>

                    {/* Questions listing moved to the right column (layout refactor). */}
                </div>

                <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex justify-end gap-3 z-10">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-6 py-2 bg-gray-300 dark:bg-slate-700 rounded"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={loading || uploadMutation.isLoading}
                        className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading || uploadMutation.isLoading
                            ? 'Saving...'
                            : isEditMode
                                ? 'Update Tool'
                                : 'Create Tool'}
                    </button>
                </div>
            </form>
        </>
    )
}

export default ToolsForm
